import { useState, useEffect } from "react";
import { Plus, Factory, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const prodTypes = { "coconut-fiber": "Coconut Fiber", "cocopeat-blocks": "Cocopeat Blocks", "coir-pith": "Coir Pith", "coir-rope": "Coir Rope" };

export default function ProductionManagement() {
  const { api, user } = useAuth();
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split("T")[0], product_type: "coconut-fiber", quantity: 0, unit: "kg", shift: "morning", grade: "A", machine_id: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const canAdd = ["owner", "manager", "supervisor"].includes(user?.role);

  const load = () => { api().get("/production").then(r => setRecords(r.data)).catch(() => {}); };
  useEffect(load, [api]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api().post("/production", form); toast.success("Production logged!"); setShowForm(false); load(); } catch { toast.error("Failed"); }
    setLoading(false);
  };

  const chartData = Object.entries(records.reduce((acc, r) => { const type = prodTypes[r.product_type] || r.product_type; acc[type] = (acc[type] || 0) + r.quantity; return acc; }, {})).map(([name, value]) => ({ name, value: Math.round(value) }));

  const totalKg = records.reduce((s, r) => s + r.quantity * (r.unit === "tons" ? 1000 : 1), 0);

  return (
    <div className="space-y-6" data-testid="production-management">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div><h2 className="text-2xl font-serif text-navy">Production</h2><p className="text-slate-400 text-sm font-sans mt-1">{records.length} production records</p></div>
        {canAdd && <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2" data-testid="log-production-btn"><Plus className="w-5 h-5" />Log Production</button>}
      </div>

      {showForm && (
        <div className="card-dashboard">
          <h3 className="text-lg font-serif text-navy mb-4">Log Production</h3>
          <form onSubmit={handleSubmit} className="grid md:grid-cols-3 gap-4" data-testid="production-form">
            <div><label className="form-label">Date</label><input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="form-input" data-testid="prod-date" /></div>
            <div><label className="form-label">Product Type</label><select value={form.product_type} onChange={e => setForm(f => ({ ...f, product_type: e.target.value }))} className="form-input" data-testid="prod-type">{Object.entries(prodTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            <div><label className="form-label">Quantity</label><input type="number" min="0" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: parseFloat(e.target.value) || 0 }))} className="form-input" data-testid="prod-qty" /></div>
            <div><label className="form-label">Unit</label><select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} className="form-input" data-testid="prod-unit"><option value="kg">Kg</option><option value="tons">Tons</option><option value="blocks">Blocks</option></select></div>
            <div><label className="form-label">Shift</label><select value={form.shift} onChange={e => setForm(f => ({ ...f, shift: e.target.value }))} className="form-input" data-testid="prod-shift"><option value="morning">Morning</option><option value="evening">Evening</option><option value="night">Night</option></select></div>
            <div><label className="form-label">Grade</label><select value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value }))} className="form-input" data-testid="prod-grade"><option value="A">A</option><option value="B">B</option><option value="C">C</option></select></div>
            <div className="md:col-span-3 flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2 border-2 border-slate-200 text-slate-600 font-sans text-sm font-medium">Cancel</button>
              <button type="submit" disabled={loading} className="btn-primary text-sm" data-testid="submit-production-btn">{loading ? "Saving..." : "Log Production"}</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-dashboard">
          <h3 className="text-lg font-serif text-navy mb-4">Production by Type</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#94A3B8" tick={{ fontSize: 11, fontFamily: "Public Sans" }} />
                <YAxis stroke="#94A3B8" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                <Tooltip contentStyle={{ fontFamily: "Public Sans", fontSize: 12, border: "1px solid #E2E8F0", borderRadius: "8px" }} />
                <Bar dataKey="value" fill="#2E7D32" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-dashboard flex flex-col justify-center items-center">
          <Factory className="w-16 h-16 text-forest/20 mb-4" />
          <p className="text-4xl font-bold text-navy font-mono">{(totalKg / 1000).toFixed(1)}</p>
          <p className="text-slate-500 text-sm font-sans mt-1">Total Tons Produced</p>
        </div>
      </div>

      <div className="card-dashboard">
        <h3 className="text-lg font-serif text-navy mb-4">Recent Records</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="production-table">
            <thead><tr className="border-b border-slate-100">
              <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Date</th>
              <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Product</th>
              <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Quantity</th>
              <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Shift</th>
              <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Grade</th>
            </tr></thead>
            <tbody>
              {records.slice(0, 20).map(r => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="p-3 font-mono text-sm">{r.date}</td>
                  <td className="p-3 text-sm font-sans">{prodTypes[r.product_type] || r.product_type}</td>
                  <td className="p-3 font-mono text-sm">{r.quantity} {r.unit}</td>
                  <td className="p-3 text-sm font-sans capitalize">{r.shift}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 text-xs font-medium ${r.grade === "A" ? "bg-green-100 text-green-700" : r.grade === "B" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>{r.grade}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
